import { Component } from "@angular/core";
import { ApiAccountTransaction, ApiUser, ApiUserOmniauth, UsersApi } from "@tytapp/api";
import { AppConfig, Shell } from "@tytapp/common";
import { UserService } from "@tytapp/user";
import { SubSink } from "subsink";

@Component({
    selector: 'tyt-account-settings',
    templateUrl: './account-settings.component.html',
    styleUrls: ['./account-settings.component.scss']
})
export class AccountSettingsComponent {
    constructor(
        private userService : UserService,
        private appConfig : AppConfig,
        private userApi : UsersApi,
        private shell : Shell
    ) {
        
    }

    subsink = new SubSink();
    user : ApiUser;
    enableYouTubeLogin = false;
    availableAccountProviders = [];
    enablePaymentHistory = false;

    async ngOnInit() {
        this.enablePaymentHistory = await this.shell.hasFeature('apps.web.enable_payment_history');
        this.subsink.add(this.userService.userChanged.subscribe(u => this.user = u));
            
        this.enableYouTubeLogin = await this.appConfig.featureEnabled('apps.web.enable_youtube_login');

        this.availableAccountProviders = [
            { id: 'facebook' },
            { id: 'google' },
            { id: 'twitter' }
        ];

        if (this.enableYouTubeLogin)
            this.availableAccountProviders.unshift({ id: 'youtube', plus: true });
    }

    ngOnDestroy() {
        this.subsink.unsubscribe();
    }
        
    shouldShowEmail(account : ApiUserOmniauth) {
        if (!account.email)
        return false;

        // if (account.email.endsWith('@pages.plusgoogle.com'))
        //   return false;

        return true;
    }
    
    nicknameForConnectedAccount(account : ApiUserOmniauth) {
        if (account.provider === 'twitter') {
        return `${account.name} (@${account.nickname})`;
        }

        if (account.name && account.nickname)
        return `${account.name} [${account.nickname}]`;
        else if (account.nickname)
        return `${account.nickname}`;
        else if (account.name)
        return `${account.name}`;
        else if (account.email)
        return account.email;
        else
        return `Anonymous Account`;
    }

    connectWithProvider(provider : string) {
        if (typeof window === 'undefined')
        return;

        let url = `${this.appConfig.platformUrl}/sign-in-with/${provider}?jwt=${encodeURIComponent(this.userService.token)}&return_url=${encodeURIComponent(`/settings/account`)}`;
        window.location.assign(url);
    }

    platformNameForProvider(provider : string) {
        let providerNames : Record<string,string> = {
        twitter: 'Twitter',
        facebook: 'Facebook',
        google: 'Google',
        youtube: 'YouTube',
        apple: 'Apple'
        };

        return providerNames[provider] || provider;
    }

    async removeConnectedAccount(account : ApiUserOmniauth) {
        let message = [];
        let vendor = this.platformNameForProvider(account.provider);

        if (account.provider === 'youtube' && this.user.membership && this.user.membership.gateway_name === 'youtube') {
        message.push(`Your current membership on TYT.com is backed by your YouTube Channel Membership. If you disconnect your account, you will no longer be able to use your YouTube membership on TYT.com.`);
        }

        if (!account.email) {
        message.push(`If you use this ${vendor} account to sign in to TYT.com in the future without first reconnecting it to your existing TYT account, a new TYT account will be made which may cause confusion.`);
        } else if (account.email != this.user.email) {
        message.push(`This ${vendor} account has a different email address (${account.email}) than your TYT account (${this.user.email}). If you sign into TYT.com using this ${vendor} account without first connecting it to your TYT account, a new account may be created which may cause confusion.`);
        } else if (account.email == this.user.email) {
        message.push(`This ${vendor} account has the same email address as your TYT account, so if you sign into TYT.com using this ${vendor} account without first connecting it to your TYT account, we'll match the accounts by email so you'll be signed into your existing TYT account. If you change the email address associated with either account in the future without first connecting the accounts, a new TYT account may be made.`);
        }

        let finalMessage = `Are you sure you wish to disconnect this account?`;

        if (message.length > 0) {
        finalMessage = `${message.map(x => `- ${x}`).join("\n")}\n\n${finalMessage}`;
        }

        if (!confirm(finalMessage)) {
        return;
        }

        try {
        await this.userApi.disconnectExternalAccount(account.uuid).toPromise();
        } catch (e) {
        console.error(`Failed while removing connected account ${account.id}:`);
        console.error(e);
        alert('An error occurred while disconnecting your external account. Please try again later and if the issue persists, contact support@tytnetwork.com.');
        return;
        }

        await this.userService.refresh();

        alert('External account has been disconnected successfully.');
    }

    transactions : ApiAccountTransaction[];
    transactionLoading : Promise<void>;
    async loadTransactions() {

        let mock = false;

        if (mock) {
            let txns = <any[]>[
                <Partial<ApiAccountTransaction>>{
                    id: 123,
                    user_description: 'One-time contribution',
                    price: 1337,
                    source_timestamp: '2021-01-21',
                    failed: false,
                    transaction_category: 'payment',
                    transaction_method: 'credit-card',
                    transaction_type: 'contribution',
                    failure_code: null,
                    failure_message: null,
                    membership: <any>{ },
                    discount: 0,
                    transaction_source: null,
                    created_at: '2021-01-21T00:00:01Z'
                },
                <Partial<ApiAccountTransaction>>{
                    id: 123,
                    user_description: 'Membership has been renewed',
                    price: 499,
                    source_timestamp: '2021-01-14',
                    failed: false,
                    transaction_category: 'payment',
                    transaction_method: 'credit-card',
                    transaction_type: 'renewal',
                    failure_code: null,
                    failure_message: null,
                    membership: <any>{ },
                    discount: 0,
                    transaction_source: 'stripe',
                    created_at: '2021-01-12T00:00:01Z'
                },
                <Partial<ApiAccountTransaction>>{
                    id: 123,
                    user_description: 'Failed to renew membership, reason: insufficient_funds',
                    price: 499,
                    source_timestamp: '2021-01-12',
                    failed: true,
                    transaction_category: 'payment',
                    transaction_method: 'credit-card',
                    transaction_type: 'renewal',
                    failure_code: null,
                    failure_message: null,
                    membership: <any>{ },
                    discount: 0,
                    transaction_source: 'stripe',
                    created_at: '2021-01-12T00:00:01Z'
                },
                <Partial<ApiAccountTransaction>>{
                    id: 123,
                    user_description: 'Purchased membership',
                    price: 499,
                    source_timestamp: '2021-12-12',
                    failed: false,
                    transaction_category: 'payment',
                    transaction_method: 'credit-card',
                    transaction_type: 'join',
                    failure_code: null,
                    failure_message: null,
                    membership: <any>{ },
                    discount: 0,
                    transaction_source: 'stripe',
                    created_at: '2021-12-12T00:00:01Z'
                },
                <Partial<ApiAccountTransaction>>{
                    id: 123,
                    user_description: 'This is a long description for testing. Porro illo illum facere eaque at inventore dolorum. Eum aperiam corporis blanditiis quo eos earum. Libero rem nobis nihil. Aut id id eum. Voluptas eveniet laudantium ut sit earum.',
                    price: 499,
                    source_timestamp: '2021-12-12',
                    failed: false,
                    transaction_category: 'something',
                    transaction_method: '',
                    transaction_type: 'else',
                    failure_code: null,
                    failure_message: null,
                    membership: <any>{ },
                    discount: 0,
                    transaction_source: 'stripe',
                    created_at: '2021-12-12T00:00:01Z'
                }
            ];

            this.transactions = [];
            for (let i = 0, max = 10; i < max; ++i)
                this.transactions = this.transactions.concat(txns);

            return;
        }

        if (!this.transactionLoading) {
            this.transactionLoading = new Promise(async (resolve, reject) => {
                this.transactions = await this.userApi.getAccountTransactions(1).toPromise();
                resolve();
            });
        }

        await this.transactionLoading;
    }

    transactionKey(txn : ApiAccountTransaction) {
        return `${txn.transaction_category}/${txn.transaction_type}`;
    }

    displayTypeOfTransaction(txn : ApiAccountTransaction) {
        let descriptions = {
            // PAYMENTS
            'payment/*': 'Payment',
            'payment/contribution': 'One-Time Contribution',
            'payment/join': 'Membership Join',
            'payment/renewal': 'Membership Renewal',
            'payment/trial-conversion': 'Membership Renewal (Trial Ended)',
            'payment/gift-renewal': 'Gift Renewal',
            'payment/dispute': 'Dispute',

            // CANCELLATION
            'cancellation/*': 'Cancellation',
            'cancellation/voluntary': 'Cancelled',
            'cancellation/expired': 'Expired',
            'cancellation/involuntary': 'Ended (Billing Issue)',
            'cancellation/confirmed': 'Cancellation Confirmed',

            // REACTIVATION
            'reactivation/*': 'Reactivation Information',
            'reactivation/info': 'Reactivation Information',
            'reactivation/reactivated': 'Membership Reactivated',
            'price_change_confirmed/*': 'Price Change Confirmed',

            // MODIFY MEMBERSHIP
            'modify_membership_product/*': 'Plan Changed',

            // CREDITS
            'credit/*': 'Credit',
            'credit/notification': 'Credit Notification',
            'credit/issued_credit': 'Membership Credit',
            'credit/pause_renewal': 'Membership Renewal Paused',
            'credit/issued_refund': 'Membership Refund Issued',

            // REFUNDS (@deprecated, only roku uses these)
            'refund/*': 'Refund',
            'refund/notification': 'Refund Notification'
        };

        return descriptions[this.transactionKey(txn)] 
            || descriptions[`${txn.transaction_category}/*`] 
            || `Miscellaneous: ${this.transactionKey(txn)}`
        ;
    }
}